import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import TimelineAssignment from '../../../containers/DailyPlanning/TimelineAssignment/TimelineAssignmentContainer';
import TimelineDropCell from '../TimelineDropCell/TimelineDropCell';
import _ from '../../../utils/lodash';
import { pixelMultiplier, cellLengthInMinutes as cellLength } from '../../../settings/scheduler';
import { parseDateUtc } from '../../../utils/dates';
import globals from '../../../globals/communityGlobals';
import gradePropTypes from '../../../propTypes/entities/grade';
import assignmentPropTypes from '../../../propTypes/entities/assignment';
import LunchBreak from '../../../containers/DailyPlanning/LunchBreak/LunchBreaks';
import { AssignmentVisitState } from '../../../enums';

function parseDate(date) {
  return moment(date || new Date(0)).utcOffset(0, true);
}

function sortAssigmentsByVisualStartDate(a, b) {
  const startA = parseDate(a.visualStartDate);
  const startB = parseDate(b.visualStartDate);
  return startA - startB;
}

function sortAssigmentsByStartDate(a, b) {
  const startA = parseDate(a.visitStarted || a.visitStartDate);
  const startB = parseDate(b.visitStarted || b.visitStartDate);
  return startA - startB;
}

const isValidRangePeriod = (start, end, startRangeDate, endRangeDate) =>
  parseDateUtc(start).isBetween(parseDateUtc(startRangeDate), parseDateUtc(endRangeDate), null, '[)') ||
  parseDateUtc(end).isBetween(parseDateUtc(startRangeDate), parseDateUtc(endRangeDate), null, '(]') ||
  (parseDateUtc(start).isBefore(startRangeDate) && parseDateUtc(end).isAfter(endRangeDate));

class TimelineAvailability extends React.Component {
  // shouldComponentUpdate = nextProps => {
  //   const {
  //     postingId,
  //     canAllocateWork,
  //     availabilityId,
  //     sortedAssignments,
  //     first,
  //     availability,
  //     previousItemEnd,
  //     clinicianGrade,
  //     clinicianId,
  //   } = this.props;
  //   let shouldUpdateBasedOnAvailability = false;
  //   const shouldUpdateBase =
  //     postingId !== nextProps.postingId ||
  //     canAllocateWork !== nextProps.canAllocateWork ||
  //     availabilityId !== nextProps.availabilityId ||
  //     first !== nextProps.first ||
  //     previousItemEnd !== nextProps.previousItemEnd ||
  //     clinicianGrade !== nextProps.clinicianGrade ||
  //     clinicianId !== nextProps.clinicianId;

  //   if (availability) {
  //     const { dropCellTargets, assignGuid, startDate, endDate } = availability;

  //     shouldUpdateBasedOnAvailability =
  //       assignGuid !== nextProps.availability.assignGuid ||
  //       sortedAssignments.map(a => a.assignGuid).join(',') !==
  //         nextProps.sortedAssignments.map(a => a.assignGuid).join(',') ||
  //       dropCellTargets.join(',') !== nextProps.availability.dropCellTargets.join(',') ||
  //       startDate !== nextProps.availability.startDate ||
  //       endDate !== nextProps.availability.endDate;
  //   }

  //   return availability !== null && (shouldUpdateBase || shouldUpdateBasedOnAvailability);
  // };

  getAviStyle = () => {
    const leftMarginInMinutes = this.calculateMargins(this.props);
    const availabilityEndDate = parseDateUtc(this.props.availability.endDate);
    const schedulerModeEndTime = parseDateUtc(this.props.schedulerModeEndTime);
    const durationEndDate =
      availabilityEndDate.diff(schedulerModeEndTime, 'minutes') >= 0 ? schedulerModeEndTime : availabilityEndDate;

    const availabilityDuration = durationEndDate.diff(parseDateUtc(this.props.availability.startDate), 'minutes');

    // +1px/-1px border fix
    const width = availabilityDuration * pixelMultiplier + 1;
    const marginLeft = leftMarginInMinutes * pixelMultiplier - !this.props.first;
    return { width: `${width}px`, marginLeft: `${marginLeft}px` };
  };

  // Add 4 mins 59 secs to the start date
  getCellEndDate = startDate =>
    startDate
      .clone()
      .add(moment.duration(cellLength, 'minutes'))
      .subtract(1, 'second')
      .format(globals.dateFormats.localApiFormat);

  getCellCheckDatesAndTravelTimes = (previousAssignmentIndex, fakeIndex, sortedAssignments, availability) => ({
    // General idea behind the functions:
    // If the assignment we select as previous/next turns out to be the currently dragged one,
    // use the previous/next one in the array as a fallback.
    // If neither exists, use availability start/end.
    endDateToCheckAndNextTravelTime: draggedAssignGuid => {
      for (let i = fakeIndex; i < sortedAssignments.length; i += 1) {
        const nextAssignment = sortedAssignments[i];
        if (nextAssignment.assignGuid !== draggedAssignGuid && nextAssignment.assignGuid !== availability.assignGuid) {
          const endDate = nextAssignment.visitStarted || nextAssignment.visitStartDate;
          if (draggedAssignGuid === availability.assignGuid) {
            return {
              endDateToCheck: nextAssignment.visualStartDate,
              nextAssignmentTravelTime: 0,
            };
          }
          return {
            endDateToCheck: endDate,
            nextAssignmentTravelTime: nextAssignment.travelTime,
          };
        }
      }
      return {
        endDateToCheck: availability.endDate,
        nextAssignmentTravelTime: 0,
      };
    },
    startDateToCheckAndPreviousTravelTime: draggedAssignGuid => {
      for (let i = previousAssignmentIndex; i >= 0; i -= 1) {
        let previousAssignment = sortedAssignments[i];
        const previousPreviousAssignment = sortedAssignments[i - 1];

        if (previousPreviousAssignment && previousPreviousAssignment.visualEndDate > previousAssignment.visualEndDate) {
          previousAssignment = previousPreviousAssignment;
        }

        if (
          previousAssignment.assignGuid !== draggedAssignGuid &&
          previousAssignment.assignGuid !== availability.assignGuid
        ) {
          const startDate = parseDate(previousAssignment.visualEndDate).isBefore(parseDate(availability.startDate))
            ? availability.startDate
            : previousAssignment.visualEndDate;
          if (draggedAssignGuid === availability.assignGuid) {
            return {
              startDateToCheck: startDate,
              previousAssignmentTravelTime: 0,
            };
          }
          return {
            startDateToCheck: startDate,
            previousAssignmentTravelTime: previousAssignment.travelTime,
          };
        }
      }
      return {
        startDateToCheck: availability.startDate,
        previousAssignmentTravelTime: 0,
      };
    },
  });

  calculateMargins = ({ previousItemEnd, availability }) => {
    const { startDate } = availability;
    return moment(startDate)
      .utcOffset(0, true)
      .diff(moment(previousItemEnd).utcOffset(0, true), 'minutes');
  };

  calculateEndDate = (availabilityEndDate, schedulerModeEndTime) => {
    const endDate = parseDateUtc(availabilityEndDate);
    const schedulerEndDate = parseDateUtc(schedulerModeEndTime);

    return endDate.diff(schedulerEndDate, 'minutes') <= 0 ? endDate : schedulerEndDate;
  };

  generateCells = () => {
    const {
      availability,
      canAllocateWork,
      availability: { dropCellTargets },
      postingId,
      clinicianGrade,
      availabilityId,
      clinicianId,
      schedulerModeStartTime,
      schedulerModeEndTime,
    } = this.props;
    let { sortedAssignments } = this.props;
    const cells = [];
    const availabilityEndDate = this.calculateEndDate(availability.endDate, schedulerModeEndTime);

    sortedAssignments = _.filter(sortedAssignments, assignment => {
      const { startDate, endDate, visitStatus, visualStartDate, visualEndDate } = assignment;
      const isEarlyCompleted =
        visitStatus === AssignmentVisitState.Complete || visitStatus === AssignmentVisitState.Complete;
      const startTime = isEarlyCompleted ? visualStartDate : startDate;
      const endTime = isEarlyCompleted ? visualEndDate : endDate;

      const isValid = isValidRangePeriod(startTime, endTime, schedulerModeStartTime, schedulerModeEndTime);
      return isValid;
    });

    sortedAssignments.sort((a, b) => sortAssigmentsByVisualStartDate(a, b));
    const sortedAssignmentsByVisit = sortedAssignments.slice(0);
    sortedAssignmentsByVisit.sort((a, b) => sortAssigmentsByStartDate(a, b));
    const assignmentsExist = sortedAssignments.length > 0;
    // These variables will change with iterations of the loop,
    // these are just the initial values
    let currentStartDate = parseDateUtc(availability.startDate);
    let nextAssignmentIndex = 0;
    let dropCellIndex = 0;
    const assignMap = [];

    // CellProps are wrapped in a function,
    // so that they always return relevant data as the assignment index changes
    const getCellProps = () => {
      let fakeIndex = nextAssignmentIndex;
      let previousAssignmentIndex = fakeIndex - 1;
      const previousAssignment = sortedAssignments[previousAssignmentIndex];
      if (previousAssignment) {
        const isAfterTravelTime = moment.utc(previousAssignment.visualStartDate).isBefore(currentStartDate);
        const startTime =
          previousAssignment.visitStarted != null ? previousAssignment.visitStarted : previousAssignment.visitStartDate;
        const isBeforeStartTime = moment.utc(startTime).isAfter(currentStartDate);

        if (isAfterTravelTime && isBeforeStartTime) {
          previousAssignmentIndex -= 1;
          fakeIndex -= 1;
        }
      }

      return {
        targets: dropCellTargets[dropCellIndex],
        canAllocateWork,
        postingId,
        clinicianGrade,
        clinicianId,
        startDate: currentStartDate.format(globals.dateFormats.localApiFormat),
        endDate: this.getCellEndDate(currentStartDate),
        key: `timelineDropCell${currentStartDate}${availabilityId}`,
        // Setting things here saves resources inside 'canDrop' of the drop cells
        ...this.getCellCheckDatesAndTravelTimes(
          previousAssignmentIndex,
          fakeIndex,
          sortedAssignmentsByVisit,
          availability,
        ),
      };
    };
    // We'll call this to move currentStartDate forward by a single cell's length
    const updateCurrentStartDate = () => {
      dropCellIndex += 1;
      currentStartDate = currentStartDate.add(moment.duration(cellLength, 'minutes'));
    };

    const updateAssignMap = (index, count, travelCellOffset) => {
      for (let i = index + travelCellOffset; i < index + count; i += 1) {
        assignMap[i] = true;
      }
    };

    const addVisit = (nextAssignment, outOfAvailabilityOffset) => {
      if (assignmentsExist && nextAssignment) {
        if (currentStartDate.isSameOrAfter(parseDateUtc(nextAssignment.visualStartDate))) {
          // currentStartDate == nextAssignment.startDate means we
          // should add the assignment and drop cells underneath
          // (orAfter serves only as a fallback, shouldn't happen under normal circumstances)
          const { assignGuid, postcode } = nextAssignment;
          if (assignGuid === availability.assignGuid) {
            const restStart = parseDateUtc(availability.restStart);
            const schedulerModeStart = parseDateUtc(schedulerModeStartTime);
            const duration = restStart.isBefore(schedulerModeStart) ? restStart.diff(schedulerModeStart, 'minutes') : 0;
            cells.push(
              <LunchBreak
                key={availabilityId}
                postcode={nextAssignment.postcode}
                availabilityId={availabilityId}
                assignGuid={availability.assignGuid}
                restStartDate={availability.restStart}
                restDuration={availability.restDuration + duration}
                isCollapsed={nextAssignment.isCollapsed}
                isOverlapping={nextAssignment.isOverlapping}
              />,
            );
          } else {
            const isOutsideTimeFrame =
              parseDateUtc(nextAssignment.visualStartDate).diff(parseDateUtc(schedulerModeStartTime), 'minutes') < 0 &&
              nextAssignment.visitStatus === AssignmentVisitState.Complete;

            cells.push(
              // We pass only the assignmentId to the component,
              // since it's a container and will get the relevant data from the store by itself
              <TimelineAssignment
                key={`timelineAssignment${assignGuid}`}
                assignGuid={assignGuid}
                postcode={postcode}
                postingId={postingId}
                canAllocateWork={canAllocateWork}
                outOfAvailabilityOffset={isOutsideTimeFrame ? 0 : outOfAvailabilityOffset}
                schedulerModeStartTime={schedulerModeStartTime}
                schedulerModeEndTime={schedulerModeEndTime}
              />,
            );
          }
          const visualStartDate = parseDateUtc(nextAssignment.visualStartDate);
          const visualEndDate = parseDateUtc(nextAssignment.visualEndDate);
          const visitStartDate = parseDateUtc(nextAssignment.visitStarted || nextAssignment.visitStartDate);
          const travelCellOffset = visitStartDate.diff(visualStartDate, 'minutes') / cellLength;
          if (outOfAvailabilityOffset === 0) {
            updateAssignMap(
              dropCellIndex,
              visualEndDate.diff(visualStartDate, 'minutes') / cellLength,
              travelCellOffset,
            );
          }

          nextAssignmentIndex += 1;
          // // Airbnb hates loops for some reason
          // // eslint-disable-next-line no-continue
        }
      }
    };

    // Main loop, will iterate until currentStartDate == availabilityEndDate
    do {
      // Push the Assignments
      const nextAssignment = sortedAssignments[nextAssignmentIndex];
      if (nextAssignment) {
        // const isBetweenTimeFrame =
        //   parseDateUtc(availability.startDate).diff(parseDateUtc(schedulerModeStartTime), 'minutes') > 0 &&
        //   nextAssignment.visitStatus === AssignmentVisitState.Complete;

        // const minutesBeforeAvailabilityStart = isBetweenTimeFrame
        //   ? parseDateUtc(availability.startDate).diff(parseDateUtc(nextAssignment.visualStartDate), 'minutes')
        //   : 0;
        const minutesBeforeAvailabilityStart = parseDateUtc(availability.startDate).diff(
          parseDateUtc(nextAssignment.visualStartDate),
          'minutes',
        );
        const outOfAvailabilityOffset = minutesBeforeAvailabilityStart > 0 ? minutesBeforeAvailabilityStart : 0;
        addVisit(nextAssignment, outOfAvailabilityOffset);
        if (minutesBeforeAvailabilityStart > 0) {
          // eslint-disable-next-line no-continue
          continue;
        }
      }

      // No assignment logic to handle,
      // generate cells until we hit nextAssignment's start date or availabilityEndDate
      cells.push(<TimelineDropCell {...getCellProps()} isAssigned={assignMap[dropCellIndex]} />);

      updateCurrentStartDate();
    } while (currentStartDate.isBefore(availabilityEndDate));
    return cells;
  };

  render() {
    if (!this.props.availability) return null;

    const { availabilityId } = this.props;
    return (
      <div id={`timelineAvailability${availabilityId}`} className="availability" style={this.getAviStyle()}>
        {this.generateCells()}
      </div>
    );
  }
}

TimelineAvailability.defaultProps = {
  itemType: null,
  sortedAssignments: [],
};

TimelineAvailability.propTypes = {
  postingId: PropTypes.number.isRequired,
  canAllocateWork: PropTypes.bool.isRequired,
  availabilityId: PropTypes.number.isRequired,
  sortedAssignments: PropTypes.arrayOf(assignmentPropTypes),
  availability: PropTypes.shape({
    restStart: PropTypes.string,
    restDuration: PropTypes.number,
    dropCellTargets: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    duration: PropTypes.number,
    start: PropTypes.string,
    end: PropTypes.string,
    id: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  // Used for calculating margins and getting relevant activites
  /* eslint-disable react/no-unused-prop-types */
  first: PropTypes.bool.isRequired,
  previousItemEnd: PropTypes.string.isRequired,
  clinicianGrade: gradePropTypes.isRequired,
  clinicianId: PropTypes.number.isRequired,
  schedulerModeEndTime: PropTypes.string.isRequired,
  schedulerModeStartTime: PropTypes.string.isRequired,
  /* eslint-enable */
};

export default TimelineAvailability;
